import { ReactElement, useContext, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { getStudySlug } from "../../../../helper/study";
import { getDefaultTabPath } from "../../../../navigation/studyTabs";
import AuthContext from "../../../../contexts/AuthContext";
import { useConfig } from "../../../../contexts/ConfigContext";
import useToast from "../../../../hooks/useToast";
import { useHomeContext } from "../../../../pages/Home/HomeContent";
import { login } from "../../../../services/user.service";
import {
  getPasswordSignInPlaceholder,
  getRoleAccessEnabledIdentifierKeys,
} from "../../../../helper/roleAccess";
import { IdentificationMethodEnum, isPasswordSignInMethod } from "../../../../types/profile";
import Container from "../../../atoms/Container";
import Subtitle from "../../../atoms/Subtitle";
import VeriticalCenter from "../../../atoms/VerticalCenter";
import EmailLoginFields from "../../../molecules/Home/Login/EmailLoginFields";
import NextPrevButtons from "../../../molecules/NextPrevButtons";
import SecretQuestionsForm from "./SecretQuestionsForm";
import ForgotPwdModal from "../../../../pages/Profile/ForgotPasswordModal";
import useQuery from "../../../../hooks/useQuery";
import { useResonanceLayout } from '../../ResonanceContext/ResonanceLayoutContext';
import { FormGroup } from "../../../atoms/LoginTextField";

const DisplayLogin = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const query = useQuery();
  const invitation_token = query.get("invitation");

  const { selectedRole } = useConfig();
  const { goPrevious, welcomeData } = useHomeContext();

  const access = selectedRole.access;
  const studyId = welcomeData.id;
  const roleId = selectedRole.id;
  const method = access.signInMode;
  const identification_questions = access.secretQuestions;
  const enabledIdentifierKeys = useMemo(
    () => getRoleAccessEnabledIdentifierKeys(access),
    [access]
  );
  const isPasswordMethod = isPasswordSignInMethod(method);
  const isEmailOnlyPasswordSignIn = isPasswordMethod
    && enabledIdentifierKeys.length === 1
    && enabledIdentifierKeys[0] === 'email';
  const loginPlaceholder = useMemo(
    () => getPasswordSignInPlaceholder(access, t, i18n.resolvedLanguage ?? i18n.language),
    [access, i18n.language, i18n.resolvedLanguage, t]
  );
  // Slug comes from config (single-study) or the URL (multi-study); the route param is
  // absent on the slug-less "/" landing, so resolve it via getStudySlug() instead.
  const study_slug = getStudySlug();
  const [isForgotPwdModalOpen, setIsForgotPwdModalOpen] = useState(false);

  const { startSession } = useContext(AuthContext);
  const history = useHistory();
  const present = useToast();

  const { setResonanceLayout } = useResonanceLayout();

  useEffect(() => {
    setResonanceLayout({ 
      title: "home_login_title",
      welcomePage: false,
     });
  }, []);

  // TODO: specify type
  const handleConnect = async (body: any) => {
    try {
      const { data } = await login(studyId, roleId, { ...body, invitation_token });
      // Role-aware landing with the role startSession RESOLVED (the real one —
      // cross-role login is allowed API-side). Also replaces the old dead
      // "/{slug}/home" push that only worked via the router fallback.
      const sessionRole = await startSession(data.token);
      history.replace(getDefaultTabPath(study_slug, sessionRole?.name));
    } catch (e) {
      const error = e as AxiosError;
      const status = error.response?.status;
      present(
        status === 429
          ? t("home_login_too_many_attempts")
          : status === 401
            ? t("home_login_incorrect")
            : t("common_server_error_title")
      );
      console.error(e);
    }
  };

  return (
    <Container>
      <VeriticalCenter>
        <FormGroup>
          <Subtitle>{t("home_login_subtitle")}</Subtitle>
        </FormGroup>

        {isPasswordMethod && (
          <EmailLoginFields
            onConnect={handleConnect}
            withNickName={!isEmailOnlyPasswordSignIn}
            loginPlaceholder={loginPlaceholder}
          />
        )}
        {method === IdentificationMethodEnum.SECRET_QUESTIONS && (
          <SecretQuestionsForm
            onConnect={handleConnect}
            secretQuestions={identification_questions}
          />
        )}
        {isPasswordMethod && <div onClick={() => setIsForgotPwdModalOpen(true)} style={{ textAlign: "center", marginTop: "20px", cursor: "pointer", textDecoration: "underline" }}>
              {t("home_forgot_password")}
        </div>}
      </VeriticalCenter>
      <NextPrevButtons onPreviousClick={goPrevious} />
      <ForgotPwdModal 
          isOpen={isForgotPwdModalOpen}
          onDismiss={() => setIsForgotPwdModalOpen(false)}
        />
    </Container>
  );
};

export default DisplayLogin;
